import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Check, FlaskConical, Loader2, Pencil, Plus, Save, Search, Trash2, X} from 'lucide-react';
import {toast} from 'react-toastify';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminPatientSearchResult, AdminSyntheticResultsResponse} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';

interface Props {
    open: boolean;
    patient: AdminPatientSearchResult | null;
    onClose: () => void;
}

interface DraftRow {
    key: number;
    assayCode: string;
    value: string;
    units: string;
}

const sourceLabel = {elis: 'ELIS result', ohd: 'OHD result'} as const;

const errorMessage = (error: unknown, fallback: string): string => {
    const candidate = error as {response?: {data?: {error?: string}}};
    return candidate.response?.data?.error ?? fallback;
};

const todayDateString = (): string => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const emptyRow = (key: number): DraftRow => ({key, assayCode: '', value: '', units: ''});

/** Identifies the single existing result currently open for inline editing. */
interface EditTarget {
    orderId: number;
    assayCode: string;
}

export const AdminSyntheticResultsModal: React.FC<Props> = ({open, patient, onClose}) => {
    const queryClient = useQueryClient();
    const queryKey = ['admin-synthetic-results', patient?.id];
    const nextRowKey = useRef(1);

    const [collectedOn, setCollectedOn] = useState(todayDateString);
    const [rows, setRows] = useState<DraftRow[]>([emptyRow(0)]);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState<EditTarget | null>(null);
    const [editValue, setEditValue] = useState('');
    const [editUnits, setEditUnits] = useState('');

    const resultsQuery = useQuery({
        queryKey,
        enabled: open && Boolean(patient),
        queryFn: () => AdminService.getSyntheticResults(patient!.id),
    });

    useEffect(() => {
        if (!open) {
            setCollectedOn(todayDateString());
            nextRowKey.current = 1;
            setRows([emptyRow(0)]);
            setSearch('');
            setEditing(null);
        }
    }, [open]);

    const updateCache = (response: AdminSyntheticResultsResponse) => {
        queryClient.setQueryData(queryKey, response);
    };

    const createMutation = useMutation({
        mutationFn: (results: Array<{assayCode: string; value: number; units?: string | null}>) =>
            AdminService.createSyntheticOrder(patient!.id, collectedOn, results),
        onSuccess: (response) => {
            updateCache(response);
            toast.success('Synthetic results created');
            nextRowKey.current = 1;
            setRows([emptyRow(0)]);
        },
        onError: (error) => toast.error(errorMessage(error, 'Unable to create synthetic results')),
    });

    const updateMutation = useMutation({
        mutationFn: (input: {orderId: number; assayCode: string; value: number; units: string | null}) =>
            AdminService.updateSyntheticResult(patient!.id, input.orderId, {
                assayCode: input.assayCode,
                value: input.value,
                units: input.units,
            }),
        onSuccess: (response) => {
            updateCache(response);
            toast.success('Synthetic result updated');
            setEditing(null);
        },
        onError: (error) => toast.error(errorMessage(error, 'Unable to update synthetic result')),
    });

    const deleteMutation = useMutation({
        mutationFn: (orderId: number) => AdminService.deleteSyntheticOrder(patient!.id, orderId),
        onSuccess: (response) => {
            updateCache(response);
            toast.success('Synthetic order deleted');
        },
        onError: (error) => toast.error(errorMessage(error, 'Unable to delete synthetic order')),
    });

    const assays = resultsQuery.data?.assays ?? [];

    // Synthetic rows come back without a usedByBioMatch flag, so the badge is
    // derived from the same assay catalog the picker above uses. Codes are
    // compared lowercased because the API matches assay codes case-insensitively.
    const biomatchCodes = useMemo(
        () =>
            new Set(
                (resultsQuery.data?.assays ?? [])
                    .filter((assay) => assay.usedByBioMatch)
                    .map((assay) => assay.code.toLowerCase())
            ),
        [resultsQuery.data?.assays]
    );

    const addRow = () => {
        const key = nextRowKey.current++;
        setRows((current) => [...current, emptyRow(key)]);
    };

    const removeRow = (key: number) => {
        setRows((current) => (current.length === 1 ? current : current.filter((row) => row.key !== key)));
    };

    const updateRow = (key: number, patch: Partial<DraftRow>) => {
        setRows((current) => current.map((row) => (row.key === key ? {...row, ...patch} : row)));
    };

    const assayOptionsForRow = (rowKey: number) => {
        const usedElsewhere = new Set(
            rows.filter((row) => row.key !== rowKey && row.assayCode).map((row) => row.assayCode)
        );
        return assays.filter((assay) => !usedElsewhere.has(assay.code));
    };

    const startEdit = (orderId: number, result: {assayCode: string; value: number; units: string | null}) => {
        setEditing({orderId, assayCode: result.assayCode});
        setEditValue(String(result.value));
        setEditUnits(result.units ?? '');
    };

    const submitEdit = () => {
        if (!editing) return;
        const value = Number(editValue);
        if (editValue.trim() === '' || !Number.isFinite(value)) {
            toast.error('Enter a numeric value');
            return;
        }
        updateMutation.mutate({
            orderId: editing.orderId,
            assayCode: editing.assayCode,
            value,
            units: editUnits.trim() || null,
        });
    };

    // Escape has to stop here: the surrounding headlessui Dialog closes the
    // whole modal on Escape, which would throw away the edit AND the modal.
    const handleEditKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitEdit();
        }
        if (event.key === 'Escape') {
            event.stopPropagation();
            setEditing(null);
        }
    };

    const handleSave = () => {
        if (!collectedOn) {
            toast.error('Choose a collection date');
            return;
        }
        const results: Array<{assayCode: string; value: number; units?: string | null}> = [];
        for (const row of rows) {
            const value = Number(row.value);
            if (!row.assayCode || !Number.isFinite(value)) {
                toast.error('Choose a test and enter a numeric value for every result');
                return;
            }
            results.push({assayCode: row.assayCode, value, units: row.units || null});
        }
        createMutation.mutate(results);
    };

    const sortedOrders = useMemo(() => {
        const orders = resultsQuery.data?.syntheticOrders ?? [];
        return [...orders]
            .sort((a, b) => {
                if (a.collectedOn !== b.collectedOn) return b.collectedOn.localeCompare(a.collectedOn);
                return b.orderId - a.orderId;
            })
            .map((order) => ({
                ...order,
                results: [...order.results].sort((a, b) => a.assayName.localeCompare(b.assayName)),
            }));
    }, [resultsQuery.data?.syntheticOrders]);

    const visibleEffectiveResults = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const effective = resultsQuery.data?.effectiveResults ?? [];
        if (!needle) return effective;
        return effective.filter(
            (result) =>
                result.assayName.toLowerCase().includes(needle) || result.assayCode.toLowerCase().includes(needle)
        );
    }, [resultsQuery.data?.effectiveResults, search]);

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                <div className="flex items-start justify-between border-b border-mld-panel-border px-6 py-4">
                                    <div>
                                        <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-mld-menu-text">
                                            <FlaskConical className="h-5 w-5 text-mld-primary" />
                                            Synthetic Test Results
                                        </Dialog.Title>
                                        <p className="mt-1 text-sm text-mld-text-secondary">
                                            {[patient?.firstName, patient?.lastName].filter(Boolean).join(' ')}
                                        </p>
                                    </div>
                                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                                        Dev & staging only
                                    </span>
                                </div>

                                <div className="space-y-5 px-6 py-5">
                                    <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                                        Creates a lab order dated as above. These results appear on the patient
                                        dashboard, trend charts and BioMatch exactly like real results.
                                    </div>

                                    {resultsQuery.isLoading ? (
                                        <div className="flex h-56 items-center justify-center gap-2 text-sm text-mld-muted-text">
                                            <Loader2 className="h-5 w-5 animate-spin" />
                                            Loading test results...
                                        </div>
                                    ) : resultsQuery.isError ? (
                                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                            Unable to load this patient&apos;s test results.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="rounded-xl border border-mld-panel-border bg-[var(--mld-background)] p-4">
                                                <div className="mb-3 flex items-center gap-2 font-semibold text-mld-menu-text">
                                                    <Plus className="h-4 w-4" />
                                                    New synthetic order
                                                </div>

                                                <div className="mb-4 max-w-xs">
                                                    <label
                                                        htmlFor="synthetic-collected-on"
                                                        className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Collection date
                                                    </label>
                                                    <input
                                                        id="synthetic-collected-on"
                                                        type="date"
                                                        required
                                                        max={todayDateString()}
                                                        value={collectedOn}
                                                        onChange={(event) => setCollectedOn(event.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    {rows.map((row) => (
                                                        <div
                                                            key={row.key}
                                                            className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1fr)_140px_140px_auto]">
                                                            <div>
                                                                <label
                                                                    htmlFor={`synthetic-assay-${row.key}`}
                                                                    className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                                    Test
                                                                </label>
                                                                <select
                                                                    id={`synthetic-assay-${row.key}`}
                                                                    value={row.assayCode}
                                                                    onChange={(event) =>
                                                                        updateRow(row.key, {
                                                                            assayCode: event.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary">
                                                                    <option value="">Choose a test...</option>
                                                                    {assayOptionsForRow(row.key).map((assay) => (
                                                                        <option key={assay.code} value={assay.code}>
                                                                            {assay.usedByBioMatch ? '★ ' : ''}
                                                                            {assay.name} ({assay.code})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label
                                                                    htmlFor={`synthetic-value-${row.key}`}
                                                                    className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                                    Value
                                                                </label>
                                                                <input
                                                                    id={`synthetic-value-${row.key}`}
                                                                    type="number"
                                                                    step="any"
                                                                    placeholder="Result"
                                                                    value={row.value}
                                                                    onChange={(event) =>
                                                                        updateRow(row.key, {
                                                                            value: event.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    htmlFor={`synthetic-units-${row.key}`}
                                                                    className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                                    Units
                                                                </label>
                                                                <input
                                                                    id={`synthetic-units-${row.key}`}
                                                                    placeholder="Optional"
                                                                    value={row.units}
                                                                    onChange={(event) =>
                                                                        updateRow(row.key, {
                                                                            units: event.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                                                />
                                                            </div>
                                                            <div className="flex items-end">
                                                                <button
                                                                    type="button"
                                                                    disabled={rows.length === 1}
                                                                    onClick={() => removeRow(row.key)}
                                                                    aria-label="Remove result"
                                                                    className="inline-flex items-center justify-center rounded-md border border-mld-panel-border px-3 py-2 text-mld-text-secondary hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40">
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="mt-3 flex items-center justify-between">
                                                    <button
                                                        type="button"
                                                        onClick={addRow}
                                                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-mld-primary hover:opacity-80">
                                                        <Plus className="h-4 w-4" />
                                                        Add another result
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={createMutation.isPending}
                                                        onClick={handleSave}
                                                        className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                                                        {createMutation.isPending ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Save className="h-4 w-4" />
                                                        )}
                                                        Save
                                                    </button>
                                                </div>
                                                <p className="mt-2 text-xs text-mld-muted-text">
                                                    Tests marked ★ are currently used by at least one BioMatch rule.
                                                </p>
                                            </div>

                                            <div>
                                                <h3 className="font-semibold text-mld-menu-text">
                                                    Existing synthetic orders
                                                </h3>
                                                <p className="text-xs text-mld-muted-text">
                                                    Delete removes the whole order, not one value.
                                                </p>
                                                <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-mld-panel-border">
                                                    <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                                                        <thead className="sticky top-0 bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.12em] text-mld-muted-text">
                                                            <tr>
                                                                <th className="px-4 py-2">Test</th>
                                                                <th className="w-36 px-4 py-2">Result</th>
                                                                <th className="w-28 px-4 py-2">Units</th>
                                                                <th className="w-28 px-4 py-2">Collected</th>
                                                                <th className="w-28 px-4 py-2 text-right">Order</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-mld-panel-border bg-white">
                                                            {sortedOrders.length === 0 ? (
                                                                <tr>
                                                                    <td
                                                                        colSpan={5}
                                                                        className="px-4 py-8 text-center text-mld-muted-text">
                                                                        No synthetic results yet.
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                sortedOrders.flatMap((order) =>
                                                                    order.results.map((result, index) => {
                                                                        const isEditing =
                                                                            editing?.orderId === order.orderId &&
                                                                            editing.assayCode === result.assayCode;
                                                                        return (
                                                                            <tr
                                                                                className="group"
                                                                                key={`${order.orderId}-${result.assayCode}`}>
                                                                                <td className="px-4 py-3">
                                                                                    <div className="font-medium text-mld-menu-text">
                                                                                        {result.assayName}
                                                                                    </div>
                                                                                    <div className="flex items-center gap-2 text-xs text-mld-muted-text">
                                                                                        <span>{result.assayCode}</span>
                                                                                        {biomatchCodes.has(
                                                                                            result.assayCode.toLowerCase()
                                                                                        ) ? (
                                                                                            <span className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-700">
                                                                                                BioMatch rule
                                                                                            </span>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </td>
                                                                                {isEditing ? (
                                                                                    <td
                                                                                        colSpan={2}
                                                                                        className="px-4 py-3">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <input
                                                                                                aria-label={`Value for ${result.assayName}`}
                                                                                                autoFocus
                                                                                                type="number"
                                                                                                step="any"
                                                                                                value={editValue}
                                                                                                onChange={(event) =>
                                                                                                    setEditValue(
                                                                                                        event.target
                                                                                                            .value
                                                                                                    )
                                                                                                }
                                                                                                onKeyDown={
                                                                                                    handleEditKeyDown
                                                                                                }
                                                                                                className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                                                                            />
                                                                                            <input
                                                                                                aria-label={`Units for ${result.assayName}`}
                                                                                                placeholder="Units"
                                                                                                value={editUnits}
                                                                                                onChange={(event) =>
                                                                                                    setEditUnits(
                                                                                                        event.target
                                                                                                            .value
                                                                                                    )
                                                                                                }
                                                                                                onKeyDown={
                                                                                                    handleEditKeyDown
                                                                                                }
                                                                                                className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                aria-label="Save result"
                                                                                                disabled={
                                                                                                    updateMutation.isPending
                                                                                                }
                                                                                                onClick={submitEdit}
                                                                                                className="inline-flex items-center justify-center rounded-md p-1.5 text-mld-primary hover:bg-mld-background disabled:opacity-40">
                                                                                                {updateMutation.isPending ? (
                                                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                                                ) : (
                                                                                                    <Check className="h-4 w-4" />
                                                                                                )}
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                aria-label="Cancel edit"
                                                                                                onClick={() =>
                                                                                                    setEditing(null)
                                                                                                }
                                                                                                className="inline-flex items-center justify-center rounded-md p-1.5 text-mld-text-secondary hover:bg-mld-background">
                                                                                                <X className="h-4 w-4" />
                                                                                            </button>
                                                                                        </div>
                                                                                    </td>
                                                                                ) : (
                                                                                    <>
                                                                                        <td className="px-4 py-3 text-mld-text-secondary">
                                                                                            <span className="inline-flex items-center gap-2">
                                                                                                {result.value}
                                                                                                <button
                                                                                                    type="button"
                                                                                                    aria-label={`Edit ${result.assayName}`}
                                                                                                    onClick={() =>
                                                                                                        startEdit(
                                                                                                            order.orderId,
                                                                                                            result
                                                                                                        )
                                                                                                    }
                                                                                                    className="text-mld-muted-text opacity-0 transition-opacity hover:text-mld-primary focus:opacity-100 focus:outline-none group-hover:opacity-100">
                                                                                                    <Pencil className="h-3.5 w-3.5" />
                                                                                                </button>
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-4 py-3 text-mld-text-secondary">
                                                                                            {result.units || '—'}
                                                                                        </td>
                                                                                    </>
                                                                                )}
                                                                                {index === 0 ? (
                                                                                    <>
                                                                                        <td
                                                                                            rowSpan={
                                                                                                order.results.length
                                                                                            }
                                                                                            className="px-4 py-3 align-middle text-mld-text-secondary">
                                                                                            {formatBirthdate(
                                                                                                order.collectedOn
                                                                                            )}
                                                                                        </td>
                                                                                        <td
                                                                                            rowSpan={
                                                                                                order.results.length
                                                                                            }
                                                                                            className="px-4 py-3 text-right align-middle">
                                                                                            <button
                                                                                                type="button"
                                                                                                disabled={
                                                                                                    deleteMutation.isPending
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    deleteMutation.mutate(
                                                                                                        order.orderId
                                                                                                    )
                                                                                                }
                                                                                                className="inline-flex items-center gap-1 text-xs font-semibold text-mld-text-secondary hover:text-red-600">
                                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                                                Delete
                                                                                            </button>
                                                                                        </td>
                                                                                    </>
                                                                                ) : null}
                                                                            </tr>
                                                                        );
                                                                    })
                                                                )
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between gap-4">
                                                <div>
                                                    <h3 className="font-semibold text-mld-menu-text">
                                                        Real imported results
                                                    </h3>
                                                    <p className="text-xs text-mld-muted-text">
                                                        ELIS and OHD values. Synthetic values have priority.
                                                    </p>
                                                </div>
                                                <label className="relative block w-64">
                                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-mld-muted-text" />
                                                    <input
                                                        aria-label="Search results"
                                                        value={search}
                                                        onChange={(event) => setSearch(event.target.value)}
                                                        placeholder="Find a test"
                                                        className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
                                                    />
                                                </label>
                                            </div>

                                            <div className="max-h-[32vh] overflow-auto rounded-lg border border-mld-panel-border">
                                                <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                                                    <thead className="sticky top-0 bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.12em] text-mld-muted-text">
                                                        <tr>
                                                            <th className="px-4 py-2">Test</th>
                                                            <th className="w-36 px-4 py-2">Result</th>
                                                            <th className="w-28 px-4 py-2">Units</th>
                                                            <th className="w-28 px-4 py-2">Recorded</th>
                                                            <th className="w-32 px-4 py-2">Source</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-mld-panel-border bg-white">
                                                        {visibleEffectiveResults.length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={5}
                                                                    className="px-4 py-8 text-center text-mld-muted-text">
                                                                    No results found.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            visibleEffectiveResults.map((result) => (
                                                                <tr key={result.assayCode}>
                                                                    <td className="px-4 py-3">
                                                                        <div className="font-medium text-mld-menu-text">
                                                                            {result.assayName}
                                                                        </div>
                                                                        <div className="flex items-center gap-2 text-xs text-mld-muted-text">
                                                                            <span>{result.assayCode}</span>
                                                                            {result.usedByBioMatch ? (
                                                                                <span className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-700">
                                                                                    BioMatch rule
                                                                                </span>
                                                                            ) : null}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-mld-text-secondary">
                                                                        {result.value}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-mld-text-secondary">
                                                                        {result.units || '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-mld-text-secondary">
                                                                        {result.recordedAt
                                                                            ? new Date(
                                                                                  result.recordedAt
                                                                              ).toLocaleDateString()
                                                                            : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-mld-text-secondary">
                                                                        {sourceLabel[result.source]}
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-2 border-t border-mld-panel-border px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded-md border border-mld-panel-border px-4 py-2 text-sm font-semibold text-mld-text-secondary">
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AdminSyntheticResultsModal;
